import { useState } from 'react'
import { gql } from 'graphql-request'
import { UserPlus, MoreHorizontal } from 'lucide-react'

import { useCurrentUser } from '@contexts/currentUser'

import { useQuery, useMutation } from '@hooks/graphql'
import { useSubscription } from '../../../hooks/subscription'
import Spinner from '@components/Spinner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@components/ui/table'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import Pill from '@components/Pill'
import { Switch } from '@components/ui/switch'
import { Label } from '@components/ui/label'
import Pagination from '@components/Pagination'

import AddStudentsModal from '../../../components/AddStudentsModal'
import NoResults from './NoResults'
import InviteStudentsButton from './InviteStudentsButton'
// import EditStudentProfileModal from './EditStudentProfileModal'

const STUDENT_PROFILES_QUERY = gql`
  query classroom($id: ID!, $showArchived: Boolean, $page: Int!) {
    classroom(id: $id) {
      research
      paginatedStudents(showArchived: $showArchived, page: $page, perPage: 10) {
        hasNextPage
        hasPreviousPage
        pagesCount
        nodesCount
        nodes {
          role
          deactivated
          profile {
            id
            fullName
            firstName
            lastName
            email
          }
          classroomEnrollment {
            researchConsent
          }
        }
      }
    }
  }
`

const UPDATE_STUDENT_MEMBERSHIP_MUTATION = gql`
  mutation updateMembership($input: UpdateMembershipInput!) {
    updateMembership(input: $input) {
      membership {
        deactivated
      }
    }
  }
`

const REMOVE_STUDENT_FROM_CLASSROOM_MUTATION = gql`
  mutation removeStudentFromClassroom($input: RemoveStudentFromClassroomInput!) {
    removeStudentFromClassroom(input: $input) {
      success
      errors
    }
  }
`

const StudentProfilesList = ({ id, name, shortCode }) => {
  // const [selectedStudent, setSelectedStudent] = useState(null)
  const [showArchived, setShowArchived] = useState(false)
  const [page, setPage] = useState(1)
  const { hasProFeatures } = useSubscription()
  const [showInviteModal, setShowInviteModal] = useState(false)

  const { isLoading, refetch, data } = useQuery({
    queryKey: ['studentProfiles', id, showArchived, page],
    gqlQuery: STUDENT_PROFILES_QUERY,
    variables: {
      id,
      showArchived,
      page
    }
  })
  const classroom = data?.classroom || {}
  const { paginatedStudents = { nodes: [], pagesCount: 0, nodesCount: 0 } } = classroom

  const { mutate: updateStudentMembership } = useMutation({
    gqlMutation: UPDATE_STUDENT_MEMBERSHIP_MUTATION,
    onSuccess: () => refetch()
  })

  const { mutate: removeStudentFromClassroom } = useMutation({
    gqlMutation: REMOVE_STUDENT_FROM_CLASSROOM_MUTATION,
    onSuccess: () => refetch()
  })

  const { currentMembership: { role } } = useCurrentUser()

  return (
    <div className='mx-5'>
      <div className='flex items-center justify-between my-4'>
        <div className='flex items-center gap-2'>
          <Switch
            id='show-archived-students'
            checked={showArchived}
            onCheckedChange={() => setShowArchived(!showArchived)}
          />
          <Label htmlFor='show-archived-students'>Show archived students</Label>
        </div>

        <InviteStudentsButton hasProFeatures={hasProFeatures} openModal={() => setShowInviteModal(true)} />
      </div>

      <Choose>
        <When condition={isLoading}>
          <Spinner className='relative top-0 left-0 flex items-center justify-center w-full h-screen' />
        </When>

        <When condition={paginatedStudents.nodesCount < 1}>
          <NoResults openModal={() => setShowInviteModal(true)} />
        </When>

        <Otherwise>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <If condition={classroom.research}>
                  <TableHead>Research Consent</TableHead>
                </If>
                <TableHead>Role</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>

            <TableBody>
              <For each='student' of={paginatedStudents.nodes}>
                <With profile={student.profile}>
                  <TableRow key={profile.id}>
                    <TableCell>
                      {profile.fullName}
                      <If condition={student.deactivated}>
                        <Pill className='ml-3 text-sm' theme='light' label='Archived' />
                      </If>
                    </TableCell>
                    <TableCell>{profile.email}</TableCell>
                    <If condition={classroom.research}>
                      <TableCell>
                        <Choose>
                          <When condition={student.classroomEnrollment.researchConsent === true}>✅</When>
                          <When condition={student.classroomEnrollment.researchConsent === false}>❌</When>
                          <Otherwise>❓</Otherwise>
                        </Choose>
                      </TableCell>
                    </If>
                    <TableCell>{student.role.toLowerCase()}</TableCell>
                    <TableCell className='text-right'>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='ghost' className='h-8 w-8 p-0'>
                            <span className='sr-only'>Open menu</span>
                            <MoreHorizontal className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem onClick={() => removeStudentFromClassroom({ input: { classroomId: id, profileId: profile.id } })}>
                            Remove from classroom
                          </DropdownMenuItem>
                          <If condition={role === 'admin'}>
                            <Choose>
                              <When condition={student.deactivated}>
                                <DropdownMenuItem onClick={() => updateStudentMembership({ input: { profileId: profile.id, deactivated: false } })}>
                                  Unarchive account
                                </DropdownMenuItem>
                              </When>
                              <Otherwise>
                                <DropdownMenuItem onClick={() => updateStudentMembership({ input: { profileId: profile.id, deactivated: true } })} className='text-destructive'>
                                  Archive account
                                </DropdownMenuItem>
                              </Otherwise>
                            </Choose>
                          </If>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                </With>
              </For>
            </TableBody>
          </Table>

          <Pagination
            page={page}
            pagesCount={paginatedStudents.pagesCount}
            setPage={setPage}
          />

          {/* <If condition={!!selectedStudent}>
              <EditStudentProfileModal
                id={selectedStudent.id}
                firstName={selectedStudent.firstName}
                lastName={selectedStudent.lastName}
                email={selectedStudent.email}
                onClose={() => setSelectedStudent(null)}
                isOpen={!!selectedStudent}
                refetch={refetch}
              />
            </If> */}
        </Otherwise>
      </Choose>

      <AddStudentsModal
        classroomId={id}
        shortCode={shortCode}
        classroomName={name}
        closeModal={() => setShowInviteModal(false)}
        isOpen={showInviteModal}
      />
    </div>
  )
}

export default StudentProfilesList
